/* @jsx h */
import { h, Fragment } from "vue";
import type {
  CloneableTemplate,
  TextboxLocals,
  CheckboxLocals,
  SelectLocals,
  StructLocals,
  ListLocals,
  SelectOption,
  SelectOptionGroup,
  BaseLocals,
} from "./types";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isOptionGroup(
  o: SelectOption | SelectOptionGroup,
): o is SelectOptionGroup {
  return "options" in o;
}

/**
 * Creates a cloneable template from a set of slot functions.
 * Each slot can be overridden independently via template.clone({ slotName: fn }).
 */
export function makeCloneableTemplate<
  T extends BaseLocals,
  S extends Record<string, Function>,
>(
  defaultSlots: S,
  render: (locals: T, slots: S) => ReturnType<typeof h>,
): CloneableTemplate<T, S> {
  function build(slots: S): CloneableTemplate<T, S> {
    const fn = (locals: T) => render(locals, slots);
    fn.clone = (overrides: Partial<S>) => build({ ...slots, ...overrides });
    fn.slots = slots;
    return fn as CloneableTemplate<T, S>;
  }
  return build(defaultSlots);
}

// ---------------------------------------------------------------------------
// Field wrapper (label + help + error chrome shared by leaf fields)
// ---------------------------------------------------------------------------

function wrapField(opts: {
  label?: any;
  help?: any;
  error?: any;
  hasError: boolean;
  input: () => any;
}) {
  return (
    <div class="form-control w-full">
      {opts.label != null && (
        <label class="label pb-0.5">
          <span class="label-text text-sm font-medium">{opts.label}</span>
        </label>
      )}
      {opts.input()}
      {opts.help != null && (
        <label class="label pt-0.5">
          <span class="label-text-alt opacity-60">{opts.help}</span>
        </label>
      )}
      {opts.hasError && opts.error != null && (
        <label class="label pt-0.5">
          <span class="label-text-alt text-error">{opts.error}</span>
        </label>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Textbox template
// ---------------------------------------------------------------------------

export type TextboxSlots = {
  renderLabel: (locals: TextboxLocals) => any;
  renderInput: (locals: TextboxLocals) => any;
  renderHelp: (locals: TextboxLocals) => any;
  renderError: (locals: TextboxLocals) => any;
  renderWrapper: (locals: TextboxLocals, slots: TextboxSlots) => any;
};

const defaultTextboxSlots: TextboxSlots = {
  renderLabel: (locals) => locals.label,
  renderInput: (locals) => (
    <input
      class={`input input-bordered w-full${locals.hasError ? " input-error" : ""}`}
      type={locals.type || "text"}
      value={locals.value ?? ""}
      disabled={locals.disabled || undefined}
      onInput={(e: Event) =>
        locals.onChange((e.target as HTMLInputElement).value)
      }
      placeholder={locals.attrs?.placeholder}
      {...(({ placeholder, ...rest }) => rest)(locals.attrs ?? {})}
    />
  ),
  renderHelp: (locals) => locals.help,
  renderError: (locals) => (locals.hasError ? locals.error : undefined),
  renderWrapper: (locals, slots) =>
    wrapField({
      label: slots.renderLabel(locals),
      help: slots.renderHelp(locals),
      error: slots.renderError(locals),
      hasError: locals.hasError,
      input: () => slots.renderInput(locals),
    }),
};

export const textboxTemplate = makeCloneableTemplate<
  TextboxLocals,
  TextboxSlots
>(defaultTextboxSlots, (locals, slots) => slots.renderWrapper(locals, slots));

// ---------------------------------------------------------------------------
// Checkbox template
// ---------------------------------------------------------------------------

export type CheckboxSlots = {
  renderInput: (locals: CheckboxLocals) => any;
  renderHelp: (locals: CheckboxLocals) => any;
  renderError: (locals: CheckboxLocals) => any;
  renderWrapper: (locals: CheckboxLocals, slots: CheckboxSlots) => any;
};

const defaultCheckboxSlots: CheckboxSlots = {
  renderInput: (locals) => (
    <label class="label cursor-pointer justify-start gap-3">
      <input
        type="checkbox"
        class={`checkbox${locals.hasError ? " checkbox-error" : ""}`}
        checked={!!locals.value}
        disabled={locals.disabled || undefined}
        onChange={(e: Event) =>
          locals.onChange((e.target as HTMLInputElement).checked)
        }
        {...locals.attrs}
      />
      {locals.label != null && <span class="label-text">{locals.label}</span>}
    </label>
  ),
  renderHelp: (locals) => locals.help,
  renderError: (locals) => (locals.hasError ? locals.error : undefined),
  renderWrapper: (locals, slots) => (
    <div class="form-control w-full">
      {slots.renderInput(locals)}
      {locals.help != null && (
        <label class="label pt-0.5">
          <span class="label-text-alt opacity-60">
            {slots.renderHelp(locals)}
          </span>
        </label>
      )}
      {locals.hasError && locals.error != null && (
        <label class="label pt-0.5">
          <span class="label-text-alt text-error">
            {slots.renderError(locals)}
          </span>
        </label>
      )}
    </div>
  ),
};

export const checkboxTemplate = makeCloneableTemplate<
  CheckboxLocals,
  CheckboxSlots
>(defaultCheckboxSlots, (locals, slots) => slots.renderWrapper(locals, slots));

// ---------------------------------------------------------------------------
// Select template
// ---------------------------------------------------------------------------

export type SelectSlots = {
  renderLabel: (locals: SelectLocals) => any;
  renderSelect: (locals: SelectLocals) => any;
  renderHelp: (locals: SelectLocals) => any;
  renderError: (locals: SelectLocals) => any;
  renderWrapper: (locals: SelectLocals, slots: SelectSlots) => any;
};

const defaultSelectSlots: SelectSlots = {
  renderLabel: (locals) => locals.label,
  renderSelect: (locals) => {
    const value = locals.multiple
      ? Array.isArray(locals.value)
        ? locals.value
        : []
      : (locals.value ?? "");

    const onChange = (e: Event) => {
      const el = e.target as HTMLSelectElement;
      if (locals.multiple) {
        const selected = Array.from(el.selectedOptions).map((o) => o.value);
        locals.onChange(selected.length ? selected : null);
      } else {
        const v = el.value;
        locals.onChange(
          locals.nullOption !== false &&
            v === (locals.nullOption ? locals.nullOption.value : "")
            ? null
            : v,
        );
      }
    };

    return (
      <select
        class={`select select-bordered w-full${locals.hasError ? " select-error" : ""}`}
        disabled={locals.disabled || undefined}
        multiple={locals.multiple || undefined}
        value={value}
        onChange={onChange}
        {...locals.attrs}
      >
        {locals.nullOption !== false && !locals.multiple && (
          <option value={locals.nullOption ? locals.nullOption.value : ""}>
            {locals.nullOption ? locals.nullOption.text : "---"}
          </option>
        )}
        {locals.options.map((opt, i) =>
          isOptionGroup(opt) ? (
            <optgroup
              key={i}
              label={opt.label}
              disabled={opt.disabled || undefined}
            >
              {opt.options.map((o, j) => (
                <option
                  key={j}
                  value={o.value}
                  disabled={o.disabled || undefined}
                >
                  {o.text}
                </option>
              ))}
            </optgroup>
          ) : (
            <option
              key={i}
              value={opt.value}
              disabled={opt.disabled || undefined}
            >
              {opt.text}
            </option>
          ),
        )}
      </select>
    );
  },
  renderHelp: (locals) => locals.help,
  renderError: (locals) => (locals.hasError ? locals.error : undefined),
  renderWrapper: (locals, slots) =>
    wrapField({
      label: slots.renderLabel(locals),
      help: slots.renderHelp(locals),
      error: slots.renderError(locals),
      hasError: locals.hasError,
      input: () => slots.renderSelect(locals),
    }),
};

export const selectTemplate = makeCloneableTemplate<SelectLocals, SelectSlots>(
  defaultSelectSlots,
  (locals, slots) => slots.renderWrapper(locals, slots),
);

// ---------------------------------------------------------------------------
// Struct template
// ---------------------------------------------------------------------------

export type StructSlots = {
  renderLegend: (locals: StructLocals) => any;
  renderHelp: (locals: StructLocals) => any;
  renderError: (locals: StructLocals) => any;
  renderFields: (locals: StructLocals) => any;
  renderWrapper: (locals: StructLocals, slots: StructSlots) => any;
};

const defaultStructSlots: StructSlots = {
  renderLegend: (locals) => locals.legend,
  renderHelp: (locals) => locals.help,
  renderError: (locals) => (locals.hasError ? locals.error : undefined),
  renderFields: (locals) =>
    locals.order.map((key) => {
      const renderFn = locals.inputs[key];
      return renderFn ? <div key={key}>{renderFn()}</div> : null;
    }),
  renderWrapper: (locals, slots) => (
    <fieldset
      class="border  rounded-lg p-4 w-full"
      disabled={locals.disabled || undefined}
    >
      {locals.legend != null && (
        <legend class="px-2 text-sm font-semibold text-base-content/70">
          {slots.renderLegend(locals)}
        </legend>
      )}
      {locals.hasError && locals.error != null && (
        <p class="text-error text-sm mb-2">{slots.renderError(locals)}</p>
      )}
      {locals.help != null && (
        <p class="text-sm opacity-60 mb-2">{slots.renderHelp(locals)}</p>
      )}
      <div class="flex flex-col gap-3">{slots.renderFields(locals)}</div>
    </fieldset>
  ),
};

export const structTemplate = makeCloneableTemplate<StructLocals, StructSlots>(
  defaultStructSlots,
  (locals, slots) => slots.renderWrapper(locals, slots),
);

// ---------------------------------------------------------------------------
// List template
// ---------------------------------------------------------------------------

export type ListSlots = {
  renderLegend: (locals: ListLocals) => any;
  renderHelp: (locals: ListLocals) => any;
  renderError: (locals: ListLocals) => any;
  renderItem: (
    locals: ListLocals,
    item: ListLocals["items"][0],
    index: number,
  ) => any;
  renderAddButton: (locals: ListLocals) => any;
  renderWrapper: (locals: ListLocals, slots: ListSlots) => any;
};

const defaultListSlots: ListSlots = {
  renderLegend: (locals) => locals.legend,
  renderHelp: (locals) => locals.help,
  renderError: (locals) => (locals.hasError ? locals.error : undefined),
  renderItem: (locals, item, index) => (
    <div key={item.key} class="flex items-start gap-2">
      <div class="flex-1">{item.input()}</div>
      {!locals.disableOrder && (
        <div class="flex flex-col gap-0.5 pt-8">
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            disabled={index === 0 || undefined}
            onClick={() => locals.onMoveUp(index)}
            title={locals.i18n.up}
          >
            <span class="material-symbols-outlined text-sm">arrow_upward</span>
          </button>
          <button
            type="button"
            class="btn btn-ghost btn-xs"
            disabled={index === locals.items.length - 1 || undefined}
            onClick={() => locals.onMoveDown(index)}
            title={locals.i18n.down}
          >
            <span class="material-symbols-outlined text-sm">
              arrow_downward
            </span>
          </button>
        </div>
      )}
      {!locals.disableRemove && (
        <button
          type="button"
          class="btn btn-ghost btn-xs text-error mt-8"
          onClick={() => locals.onRemove(index)}
          title={locals.i18n.remove}
        >
          <span class="material-symbols-outlined text-sm">delete</span>
        </button>
      )}
    </div>
  ),
  renderAddButton: (locals) =>
    !locals.disableAdd ? (
      <button
        type="button"
        class="btn btn-outline btn-sm mt-2"
        onClick={locals.onAdd}
        disabled={locals.disabled || undefined}
      >
        <span class="material-symbols-outlined text-sm">add</span>
        {locals.i18n.add}
      </button>
    ) : null,
  renderWrapper: (locals, slots) => (
    <div class="flex flex-col w-full">
      {locals.legend != null && (
        <p class="text-sm font-semibold mb-1">{slots.renderLegend(locals)}</p>
      )}
      {locals.hasError && locals.error != null && (
        <p class="text-error text-sm mb-1">{slots.renderError(locals)}</p>
      )}
      {locals.help != null && (
        <p class="text-sm opacity-60 mb-1">{slots.renderHelp(locals)}</p>
      )}
      <div class="flex flex-col gap-2">
        {locals.items.map((item, i) => slots.renderItem(locals, item, i))}
      </div>
      {slots.renderAddButton(locals)}
    </div>
  ),
};

export const listTemplate = makeCloneableTemplate<ListLocals, ListSlots>(
  defaultListSlots,
  (locals, slots) => slots.renderWrapper(locals, slots),
);

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Toggle template — boolean field as a label-left / toggle-right row
// Use via options.toggle = true or options.template = toggleTemplate
// locals.config.description renders a subtitle below the label
// ---------------------------------------------------------------------------

export type ToggleSlots = {
  renderToggle: (locals: CheckboxLocals) => any;
  renderLabel: (locals: CheckboxLocals) => any;
  renderDescription: (locals: CheckboxLocals) => any;
  renderError: (locals: CheckboxLocals) => any;
  renderWrapper: (locals: CheckboxLocals, slots: ToggleSlots) => any;
};

const defaultToggleSlots: ToggleSlots = {
  renderToggle: (locals) => (
    <input
      type="checkbox"
      class={`toggle toggle-primary toggle-sm${locals.hasError ? " toggle-error" : ""}`}
      checked={!!locals.value}
      disabled={locals.disabled || undefined}
      onChange={(e: Event) =>
        locals.onChange((e.target as HTMLInputElement).checked)
      }
      {...locals.attrs}
    />
  ),
  renderLabel: (locals) => locals.label,
  renderDescription: (locals) => locals.config.description as any,
  renderError: (locals) => (locals.hasError ? locals.error : undefined),
  renderWrapper: (locals, slots) => (
    <div class="flex items-center justify-between gap-4 py-3 border-b border-base-200 last:border-b-0">
      <div class="flex-1 min-w-0">
        {slots.renderLabel(locals) != null && (
          <div class="text-sm font-medium text-base-content">
            {slots.renderLabel(locals)}
          </div>
        )}
        {slots.renderDescription(locals) != null && (
          <div class="text-xs text-base-content/60 mt-0.5">
            {slots.renderDescription(locals)}
          </div>
        )}
        {locals.hasError && locals.error != null && (
          <div class="text-xs text-error mt-0.5">
            {slots.renderError(locals)}
          </div>
        )}
        {locals.help != null && (
          <div class="text-xs text-base-content/50 mt-0.5">{locals.help}</div>
        )}
      </div>
      <div class="shrink-0">{slots.renderToggle(locals)}</div>
    </div>
  ),
};

export const toggleTemplate = makeCloneableTemplate<
  CheckboxLocals,
  ToggleSlots
>(defaultToggleSlots, (locals, slots) => slots.renderWrapper(locals, slots));

// ---------------------------------------------------------------------------
// Default templates registry (mirrors tcomb-form's Form.templates)
// ---------------------------------------------------------------------------

export const defaultTemplates = {
  textbox: textboxTemplate,
  checkbox: checkboxTemplate,
  select: selectTemplate,
  struct: structTemplate,
  list: listTemplate,
  toggle: toggleTemplate,
};

export type Templates = typeof defaultTemplates;
